


























































import { Vue, Component, Watch, Prop } from 'vue-property-decorator';

import { Surface } from '@/components/atoms';
import CloseModalIcon from '@/components/icons/CloseModal.vue';
import TextField from '@/components/atoms/TextField/TextField.vue';
import TextArea from '@/components/atoms/TextArea/TextArea.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';

import eventBus from '@/utils/eventBus';
import { pluralize } from '@/utils';

import BusinessService from '@/api/BusinessService';
import CoinsService from '@/api/CoinsService';
import { formatString, toNumber } from '@/utils/moneyTreatments';
import segmentIo from '@/utils/segment.io';

type FormData = {
  coinsAmount: string;
  message: string;
};

@Component({
  components: {
    CloseModalIcon,
    Surface,
    TextField,
    TextArea,
    FilledButton
  }
})
export default class AwardCoinsModal extends Vue {
  @Prop(String) customerName!: string;
  @Prop(String) customerId!: string;
  @Prop() onConfirm?: (data: any) => void;

  businessService: BusinessService = new BusinessService();
  coinsService: CoinsService = new CoinsService();

  loading = false;
  pluralize = pluralize;
  coinExchangeRatio = 0;
  form: FormData = {
    coinsAmount: '',
    message: ''
  };

  get businessInfo() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  get submitDisabled() {
    return (
      !toNumber(this.form.coinsAmount) || toNumber(this.form.coinsAmount) < 1
    );
  }

  get currencySymbol() {
    return this.$store.getters['internationalization/getCountrySettings']
      .currencySymbol;
  }

  get getAmountDolar() {
    const result = toNumber(this.form.coinsAmount) * this.coinExchangeRatio;
    return result.toFixed(4).replace(/\d(?=(\d{3})+\.)/g, '$&');
  }

  get placeholderAmountDolar() {
    const amount = toNumber(this.getAmountDolar);

    return formatString(
      Math.floor(amount * 100)
        .toString()
        .replace(/\d(?=(\d{3})+\.)/g, '$&,')
    );
  }

  created() {
    document.onkeydown = this.handleOnkeydown;

    this.getCoinExchange();
    segmentIo.modal('Award Coins');
  }

  mounted() {
    this.setFocusOnTextFieldCoins();
  }

  setFocusOnTextFieldCoins() {
    const textFieldCoinsEl = document.querySelector<HTMLElement>(
      '.text-field-coins'
    );

    if (!textFieldCoinsEl) return;

    const inputEl = textFieldCoinsEl.querySelector('input')!;

    inputEl.focus();
  }

  @Watch('$route')
  onRouteChange() {
    this.onClose();
  }

  async getCoinExchange() {
    const response = await this.businessService.getCoinExchange();

    if (response.status !== 200) return;

    this.coinExchangeRatio = response.data.coinExchangeRatio;
  }

  onlyNumber(event: KeyboardEvent) {
    if (isNaN(Number(event.key))) {
      event.preventDefault();
    }
  }

  onClose(payload?: Record<string, any>) {
    eventBus.$emit('closeModal', payload);
  }

  handleOnkeydown({ key }: KeyboardEvent) {
    if (key === 'Escape') {
      this.onClose();
    }
  }

  async handleSendCoins(payload: Record<string, any>) {
    this.loading = true;

    const response = await this.coinsService.sendCoins(payload);

    this.loading = false;

    if (response.status !== 201) return;

    eventBus.$emit('reloadCustomerDetail');
    eventBus.$emit('reloadCustomerDetailActivities');

    if (this.onConfirm) {
      this.onConfirm(payload);
    }

    this.$notification(
      `An award of ${payload.coins} ${this.pluralize(
        Number(payload.coins),
        'Coin'
      )} has been sent to ${this.customerName}`
    );
  }

  async handleSubmit() {
    this.onClose({ continueModal: true });

    const { coinsAmount, message } = this.form;

    const response = await this.$showConfirmDialog({
      title: 'Award Coins',
      description: `
        You are about to send <strong>${coinsAmount}</strong> ${this.pluralize(
        toNumber(coinsAmount),
        'Coin'
      )} to <strong>${this.customerName}</strong>
        <br />
        <br />
        Would you like to proceed?
      `,
      cancelButtonText: 'No',
      confirmButtonText: 'Yes',
      icon: 'icon-award'
    });

    if (response === 'confirm') {
      const formData = {
        coins: coinsAmount,
        message: !message
          ? `${
              this.businessInfo.name
            } has just awarded you with ${coinsAmount} ${this.pluralize(
              toNumber(coinsAmount),
              'Coin'
            )}`
          : message,
        money: this.getAmountDolar,
        placeholder: this.placeholderAmountDolar,
        customerId: this.customerId
      };

      this.handleSendCoins(formData);
    }
  }
}
